import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './OwnerManager.css';

interface Owner {
  id: number;
  user_id: number;
  building_id: number;
  flat_id: number;
  flat_number: string;
  ownership_type: 'owner' | 'co_owner' | 'legal_heir';
  ownership_percentage: number;
  purchase_date: string | null;
  purchase_price: number | null;
  current_value?: number | null;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
  notes?: string;
  is_primary_owner: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    name: string;
    email: string;
    phone: string;
  };
  building: {
    id: number;
    name: string;
    address: string;
  };
  flat: {
    id: number;
    flat_number: string;
    flat_type: string;
    bedrooms: number;
    bathrooms: number;
    area_sqft?: number;
  };
}

interface Building {
  id: number;
  name: string;
  address: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
}

interface OwnerFormData {
  user_id: number | null;
  building_id: number | null;
  flat_id: number | null;
  flat_number: string;
  ownership_type: 'owner' | 'co_owner' | 'legal_heir';
  ownership_percentage: number;
  purchase_date: string;
  purchase_price: number;
  current_value: number;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relation: string;
  notes: string;
  is_primary_owner: boolean;
  is_active: boolean;
}

const OwnerManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'edit' | 'transfer'>('list');
  const [owners, setOwners] = useState<Owner[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [buildingFilter, setBuildingFilter] = useState<number | null>(null);
  const [ownershipTypeFilter, setOwnershipTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [ownerForm, setOwnerForm] = useState<OwnerFormData>({
    user_id: null,
    building_id: null,
    flat_id: null,
    flat_number: '',
    ownership_type: 'owner',
    ownership_percentage: 100,
    purchase_date: '',
    purchase_price: 0,
    current_value: 0,
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    notes: '',
    is_primary_owner: true,
    is_active: true,
  });

  const [transferForm, setTransferForm] = useState({
    new_user_id: null as number | null,
    transfer_date: '',
    transfer_price: 0,
    notes: '',
  });

  const fetchOwners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (buildingFilter) params.append('building_id', buildingFilter.toString());
      if (ownershipTypeFilter !== 'all') params.append('ownership_type', ownershipTypeFilter);
      params.append('page', currentPage.toString());
      params.append('per_page', '15');

      const response = await api.get(`/owners?${params}`);
      
      if (response.data.success) {
        setOwners(response.data.data.data || response.data.data);
        setTotalPages(response.data.data.last_page || 1);
      } else {
        setError(response.data.message || 'Failed to fetch owners');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching owners');
      console.error('Error fetching owners:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, buildingFilter, ownershipTypeFilter, currentPage]);

  const fetchBuildings = async () => {
    try {
      const response = await api.get('/buildings');
      if (response.data.success) {
        setBuildings(response.data.data.data || response.data.data);
      }
    } catch (err) {
      console.error('Error fetching buildings:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUsers(response.data.data.data || response.data.data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'list') {
      fetchOwners();
    } else if (activeTab === 'create' || activeTab === 'edit') {
      fetchBuildings();
      fetchUsers();
    }
  }, [activeTab, fetchOwners]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Prepare form data, converting empty strings to null for optional fields
      const formData = {
        ...ownerForm,
        purchase_date: ownerForm.purchase_date || null,
        purchase_price: ownerForm.purchase_price || null,
        current_value: ownerForm.current_value || null,
        emergency_contact_name: ownerForm.emergency_contact_name || null,
        emergency_contact_phone: ownerForm.emergency_contact_phone || null,
        emergency_contact_relation: ownerForm.emergency_contact_relation || null,
        notes: ownerForm.notes || null,
      };

      if (editingOwner) {
        await api.put(`/owners/${editingOwner.id}`, formData);
      } else {
        await api.post('/owners', formData);
      }

      resetForm();
      setActiveTab('list');
      fetchOwners();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save owner');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (owner: Owner) => {
    setEditingOwner(owner);
    setOwnerForm({
      user_id: owner.user_id,
      building_id: owner.building_id,
      flat_id: owner.flat_id,
      flat_number: owner.flat_number,
      ownership_type: owner.ownership_type,
      ownership_percentage: owner.ownership_percentage,
      purchase_date: owner.purchase_date || '',
      purchase_price: owner.purchase_price || 0,
      current_value: owner.current_value || 0,
      emergency_contact_name: owner.emergency_contact_name || '',
      emergency_contact_phone: owner.emergency_contact_phone || '',
      emergency_contact_relation: owner.emergency_contact_relation || '',
      notes: owner.notes || '',
      is_primary_owner: owner.is_primary_owner,
      is_active: owner.is_active,
    });
    setActiveTab('edit');
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this ownership record?')) {
      try {
        setLoading(true);
        await api.delete(`/owners/${id}`);
        fetchOwners();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete owner');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOwner) return;

    try {
      setLoading(true);
      setError(null);

      await api.post(`/owners/${editingOwner.id}/transfer`, transferForm);

      setTransferForm({
        new_user_id: null,
        transfer_date: '',
        transfer_price: 0,
        notes: '',
      });
      setActiveTab('list');
      fetchOwners();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to transfer ownership');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingOwner(null);
    setOwnerForm({
      user_id: null,
      building_id: null,
      flat_id: null,
      flat_number: '',
      ownership_type: 'owner',
      ownership_percentage: 100,
      purchase_date: '',
      purchase_price: 0,
      current_value: 0,
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relation: '',
      notes: '',
      is_primary_owner: true,
      is_active: true,
    });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setOwnerForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const getOwnershipTypeColor = (type: string) => {
    switch (type) {
      case 'owner': return 'success';
      case 'co_owner': return 'info';
      case 'legal_heir': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'secondary';
  };

  if (loading && !owners.length) {
    return (
      <div className="owner-manager-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>Loading Owners...</h3>
          <p>Please wait while we fetch the data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="owner-manager">
      <div className="owner-header">
        <div className="header-content">
          <h1>Owner Management</h1>
          <p>Manage flat ownership records and owner details.</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => { resetForm(); setActiveTab('create'); }}>
            <i className="fas fa-plus mr-2"></i> Add New Owner
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-times-circle error-icon"></i>
          <span className="error-text">{error}</span>
          <button className="error-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <div className="owner-tabs">
        <button
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          <i className="fas fa-list mr-2"></i> Owners List
        </button>
        {(activeTab === 'create' || activeTab === 'edit') && (
          <button
            className={`tab-btn ${activeTab === 'create' || activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            <i className="fas fa-plus mr-2"></i> {editingOwner ? 'Edit Owner' : 'Create Owner'}
          </button>
        )}
        {editingOwner && (
          <button
            className={`tab-btn ${activeTab === 'transfer' ? 'active' : ''}`}
            onClick={() => setActiveTab('transfer')}
          >
            <i className="fas fa-exchange-alt mr-2"></i> Transfer Ownership
          </button>
        )}
      </div>

      <div className="owner-content">
        {activeTab === 'list' && (
          <div className="owners-list">
            <div className="owner-filters">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by owner name, email, phone, or flat number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="search-btn" onClick={() => fetchOwners()}>
                  <i className="fas fa-search"></i>
                </button>
              </div>
              <div className="filter-group">
                <select
                  value={buildingFilter || ''}
                  onChange={(e) => setBuildingFilter(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">All Buildings</option>
                  {buildings.map(building => (
                    <option key={building.id} value={building.id}>{building.name}</option>
                  ))}
                </select>
                <select
                  value={ownershipTypeFilter}
                  onChange={(e) => setOwnershipTypeFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="owner">Owner</option>
                  <option value="co_owner">Co-Owner</option>
                  <option value="legal_heir">Legal Heir</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">Loading owners...</div>
            ) : owners.length === 0 ? (
              <div className="no-owners text-center py-5">
                <i className="fas fa-user-times fa-3x text-muted mb-3"></i>
                <h3 className="text-muted">No owners found.</h3>
                <p className="text-muted">Try adjusting your search or filters, or add a new owner.</p>
              </div>
            ) : (
              <>
                <div className="owners-grid">
                  {owners.map(owner => (
                    <div key={owner.id} className="owner-card">
                      <div className="owner-card-header">
                        <h3>{owner.user.name}</h3>
                        <div className="owner-badges">
                          <span className={`badge ${getOwnershipTypeColor(owner.ownership_type)}`}>
                            {owner.ownership_type.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className={`badge ${getStatusColor(owner.is_active)}`}>
                            {owner.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                          {owner.is_primary_owner && (
                            <span className="badge primary">PRIMARY</span>
                          )}
                        </div>
                      </div>
                      <div className="owner-card-content">
                        <div className="owner-info">
                          <div className="info-item">
                            <label>Flat:</label>
                            <span>{owner.flat_number} ({owner.flat.flat_type})</span>
                          </div>
                          <div className="info-item">
                            <label>Building:</label>
                            <span>{owner.building.name}</span>
                          </div>
                          <div className="info-item">
                            <label>Email:</label>
                            <span>{owner.user.email}</span>
                          </div>
                          <div className="info-item">
                            <label>Phone:</label>
                            <span>{owner.user.phone}</span>
                          </div>
                          <div className="info-item">
                            <label>Purchase Date:</label>
                            <span>{owner.purchase_date ? new Date(owner.purchase_date).toLocaleDateString() : 'Not set'}</span>
                          </div>
                          <div className="info-item">
                            <label>Purchase Price:</label>
                            <span>৳{owner.purchase_price ? owner.purchase_price.toLocaleString() : 'Not set'}</span>
                          </div>
                          <div className="info-item">
                            <label>Current Value:</label>
                            <span>৳{owner.current_value ? owner.current_value.toLocaleString() : 'Not set'}</span>
                          </div>
                          <div className="info-item">
                            <label>Ownership:</label>
                            <span>{owner.ownership_percentage}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="owner-card-actions">
                        <button className="btn btn-info" onClick={() => handleEdit(owner)}>
                          <i className="fas fa-edit mr-2"></i> Edit
                        </button>
                        <button className="btn btn-warning" onClick={() => { setEditingOwner(owner); setActiveTab('transfer'); }}>
                          <i className="fas fa-exchange-alt mr-2"></i> Transfer
                        </button>
                        <button className="btn btn-danger" onClick={() => handleDelete(owner.id)}>
                          <i className="fas fa-trash mr-2"></i> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <div className="pagination-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {(activeTab === 'create' || activeTab === 'edit') && (
          <div className="owner-form">
            <h2>{editingOwner ? 'Edit Owner Details' : 'Add New Owner'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="user_id">Owner *</label>
                  <select
                    id="user_id"
                    name="user_id"
                    value={ownerForm.user_id || ''}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Owner</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="building_id">Building *</label>
                  <select
                    id="building_id"
                    name="building_id"
                    value={ownerForm.building_id || ''}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Building</option>
                    {buildings.map(building => (
                      <option key={building.id} value={building.id}>{building.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="flat_number">Flat Number *</label>
                  <input
                    type="text"
                    id="flat_number"
                    name="flat_number"
                    value={ownerForm.flat_number}
                    onChange={handleFormChange}
                    placeholder="e.g., 12-D"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ownership_type">Ownership Type *</label>
                  <select
                    id="ownership_type"
                    name="ownership_type"
                    value={ownerForm.ownership_type}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="owner">Owner</option>
                    <option value="co_owner">Co-Owner</option>
                    <option value="legal_heir">Legal Heir</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="ownership_percentage">Ownership Percentage *</label>
                  <input
                    type="number"
                    id="ownership_percentage"
                    name="ownership_percentage"
                    value={ownerForm.ownership_percentage}
                    onChange={handleFormChange}
                    min="0"
                    max="100"
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="purchase_date">Purchase Date</label>
                  <input
                    type="date"
                    id="purchase_date"
                    name="purchase_date"
                    value={ownerForm.purchase_date}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="purchase_price">Purchase Price</label>
                  <input
                    type="number"
                    id="purchase_price"
                    name="purchase_price"
                    value={ownerForm.purchase_price}
                    onChange={handleFormChange}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="current_value">Current Value</label>
                  <input
                    type="number"
                    id="current_value"
                    name="current_value"
                    value={ownerForm.current_value}
                    onChange={handleFormChange}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="emergency_contact_name">Emergency Contact Name</label>
                  <input
                    type="text"
                    id="emergency_contact_name"
                    name="emergency_contact_name"
                    value={ownerForm.emergency_contact_name}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="emergency_contact_phone">Emergency Contact Phone</label>
                  <input
                    type="text"
                    id="emergency_contact_phone"
                    name="emergency_contact_phone"
                    value={ownerForm.emergency_contact_phone}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="emergency_contact_relation">Emergency Contact Relation</label>
                  <input
                    type="text"
                    id="emergency_contact_relation"
                    name="emergency_contact_relation"
                    value={ownerForm.emergency_contact_relation}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="form-group full-width">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={ownerForm.notes}
                    onChange={handleFormChange}
                    rows={3}
                  ></textarea>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_primary_owner"
                      checked={ownerForm.is_primary_owner}
                      onChange={handleFormChange}
                    />
                    Primary Owner
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={ownerForm.is_active}
                      onChange={handleFormChange}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { resetForm(); setActiveTab('list'); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingOwner ? 'Update Owner' : 'Create Owner'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'transfer' && editingOwner && (
          <div className="transfer-form">
            <h2>Transfer Ownership</h2>
            <p>Transfer ownership from <strong>{editingOwner.user.name}</strong> to another user.</p>
            <form onSubmit={handleTransfer}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="new_user_id">New Owner *</label>
                  <select
                    id="new_user_id"
                    name="new_user_id"
                    value={transferForm.new_user_id || ''}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, new_user_id: parseInt(e.target.value) || null }))}
                    required
                  >
                    <option value="">Select New Owner</option>
                    {users.filter(user => user.id !== editingOwner.user_id).map(user => (
                      <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="transfer_date">Transfer Date *</label>
                  <input
                    type="date"
                    id="transfer_date"
                    name="transfer_date"
                    value={transferForm.transfer_date}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, transfer_date: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="transfer_price">Transfer Price</label>
                  <input
                    type="number"
                    id="transfer_price"
                    name="transfer_price"
                    value={transferForm.transfer_price}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, transfer_price: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group full-width">
                  <label htmlFor="transfer_notes">Transfer Notes</label>
                  <textarea
                    id="transfer_notes"
                    name="transfer_notes"
                    value={transferForm.notes}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  ></textarea>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setActiveTab('list')}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-warning">
                  Transfer Ownership
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerManager;
